import { skipToken } from "@reduxjs/toolkit/query";
import { CustomFieldResourceType } from "@repo/app-types/entities/customField";
import type { CustomFieldCondition } from "@repo/app-types/entities/filterConditions";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { SimpleMenuSection } from "@repo/ui/Menu/simpleMenuSection";
import type { FC } from "react";
import { Collection, SubmenuTrigger } from "react-aria-components";
import { useAllDataExtractionFieldDefinitionsQuery } from "../../api/entities/dataExtractionField.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useAllUserCustomFieldDefinitionsQuery } from "../../api/entities/userCustomField.js";
import { CustomFieldConditionMenu } from "./customFieldConditionMenu.js";

export interface CustomFieldConditionsMenuProps {
  playbookId?: PlaybookId;
  onSelect: (condition: CustomFieldCondition) => void;
}

export const CustomFieldConditionsMenu: FC<CustomFieldConditionsMenuProps> = (
  props,
) => {
  const { data: allCustomFieldDefinitions } =
    useAllUserCustomFieldDefinitionsQuery();
  const { data: allDataExtractionFieldDefinitions } =
    useAllDataExtractionFieldDefinitionsQuery();
  const { data: playbook } = usePlaybookByIdQuery(
    props.playbookId ?? skipToken,
  );

  const contactCustomFieldDefinitions = allCustomFieldDefinitions?.filter(
    (field) => field.resource_type === CustomFieldResourceType.CONTACT,
  );

  // Manually-set customer interaction metadata fields: user-provider grading
  // fields (company-wide, not playbook-scoped like data extraction fields).
  const customerInteractionCustomFieldDefinitions =
    allCustomFieldDefinitions?.filter(
      (field) => field.resource_type === CustomFieldResourceType.GRADING,
    );

  const playbookDataExtractionFieldIds = new Set(
    playbook?.settings.grading_data_extraction?.custom_field_definition_ids ??
      [],
  );

  // Only grading-typed data extraction fields are ingested into the
  // teammate_account_gradings custom_fields_string map alongside contact and
  // account fields, so interview-typed extraction fields can't be filtered on
  // here.
  const gradingDataExtractionFieldDefinitions =
    allDataExtractionFieldDefinitions?.filter(
      (field) =>
        field.resource_type === CustomFieldResourceType.GRADING &&
        playbookDataExtractionFieldIds.has(field.id),
    );

  // TODO: Show loading state
  return (
    <SimpleMenu aria-label="Filter by contact or conversation">
      <SimpleMenuSection label="Contact">
        <Collection items={contactCustomFieldDefinitions}>
          {(item) => (
            <SubmenuTrigger>
              <SimpleMenuItem>{item.display_name}</SimpleMenuItem>

              <SimpleMenuPopover>
                <CustomFieldConditionMenu
                  customFieldDefinition={item}
                  playbookId={props.playbookId}
                  onSelect={props.onSelect}
                />
              </SimpleMenuPopover>
            </SubmenuTrigger>
          )}
        </Collection>
      </SimpleMenuSection>
      {customerInteractionCustomFieldDefinitions &&
        customerInteractionCustomFieldDefinitions.length > 0 && (
          <SimpleMenuSection label="Customer interaction">
            <Collection items={customerInteractionCustomFieldDefinitions}>
              {(item) => (
                <SubmenuTrigger>
                  <SimpleMenuItem>{item.display_name}</SimpleMenuItem>

                  <SimpleMenuPopover>
                    <CustomFieldConditionMenu
                      customFieldDefinition={item}
                      playbookId={props.playbookId}
                      onSelect={props.onSelect}
                    />
                  </SimpleMenuPopover>
                </SubmenuTrigger>
              )}
            </Collection>
          </SimpleMenuSection>
        )}
      {gradingDataExtractionFieldDefinitions &&
        gradingDataExtractionFieldDefinitions.length > 0 && (
          <SimpleMenuSection label="Data extraction fields">
            <Collection items={gradingDataExtractionFieldDefinitions}>
              {(item) => (
                <SubmenuTrigger>
                  <SimpleMenuItem>{item.display_name}</SimpleMenuItem>

                  <SimpleMenuPopover>
                    <CustomFieldConditionMenu
                      customFieldDefinition={item}
                      playbookId={props.playbookId}
                      onSelect={props.onSelect}
                    />
                  </SimpleMenuPopover>
                </SubmenuTrigger>
              )}
            </Collection>
          </SimpleMenuSection>
        )}
    </SimpleMenu>
  );
};
